// Centralized date defaults. Locale will be sourced from preferences once
// backend support is ready; for now these remain client-side defaults.
export const DEFAULT_DATE_LOCALE = "en-US";
export const DEFAULT_DATE_FORMAT = "MM/dd/yyyy";
export const CLINICAL_DATE_FORMAT = "MMM dd yyyy";

const FORMAT_TOKEN_REGEX = /yyyy|MMMM|MMM|MM|dd/g;
const TIME_TOKEN_REGEX = /HH|hh|mm|ss|a/g;

const padTo2 = value => String(value).padStart(2, "0");

/**
 * Universal date parser. Accepts Date objects, YYYYMMDD strings, ISO strings,
 * numeric timestamps (number or numeric string). Returns a valid Date or null.
 * Prefer this over parseTimestampField when input may be YYYYMMDD format.
 * Exported as parseDateValue.
 */
const coerceToDate = value => {
  if (!value) return null;

  if (value instanceof Date && !isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === "string" && /^\d{8}$/.test(value)) {
    return parseYYYYMMDD(value);
  }

  if (typeof value === "string" && /^\d+$/.test(value)) {
    const numericTimestamp = parseInt(value, 10);
    if (!Number.isNaN(numericTimestamp)) {
      const date = new Date(numericTimestamp);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  if (typeof value === "number") {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  const parsedDate = new Date(value);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const parseDateValue = coerceToDate;

const formatDatePattern = (date, format, locale, uppercaseMonth = false) => {
  if (!date) return "";

  const monthShort = new Intl.DateTimeFormat(locale, { month: "short" }).format(
    date
  );
  const monthLong = new Intl.DateTimeFormat(locale, { month: "long" }).format(
    date
  );

  const replacements = {
    yyyy: String(date.getFullYear()),
    MM: padTo2(date.getMonth() + 1),
    dd: padTo2(date.getDate()),
    MMM: uppercaseMonth ? monthShort.toUpperCase() : monthShort,
    MMMM: uppercaseMonth ? monthLong.toUpperCase() : monthLong,
  };

  return format.replace(FORMAT_TOKEN_REGEX, token => replacements[token] || "");
};

const formatTimePattern = (date, timeFormat, locale) => {
  if (!date || !timeFormat) return "";

  const hours24 = padTo2(date.getHours());
  const hours12 = padTo2(((date.getHours() + 11) % 12) + 1);
  const minutes = padTo2(date.getMinutes());
  const seconds = padTo2(date.getSeconds());
  const dayPeriod = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
  })
    .formatToParts(date)
    .find(part => part.type === "dayPeriod")?.value;

  const replacements = {
    HH: hours24,
    hh: hours12,
    mm: minutes,
    ss: seconds,
    a: (dayPeriod || (date.getHours() >= 12 ? "PM" : "AM")).toUpperCase(),
  };

  return timeFormat.replace(
    TIME_TOKEN_REGEX,
    token => replacements[token] || ""
  );
};

/**
 * Preferred UI formatter for dates. Defaults to MM/dd/yyyy using the client-side
 * locale unless overridden. Accepts Date objects, ISO strings, timestamps, or
 * YYYYMMDD strings.
 */
export const formatDisplayDate = (
  value,
  {
    locale = DEFAULT_DATE_LOCALE,
    format = DEFAULT_DATE_FORMAT,
    includeTime = false,
    timeFormat = "HH:mm:ss",
    uppercaseMonth = false,
  } = {}
) => {
  const date = coerceToDate(value);
  if (!date) return "";

  const datePart = formatDatePattern(date, format, locale, uppercaseMonth);
  if (!includeTime) return datePart;

  const timePart = formatTimePattern(date, timeFormat, locale);
  return timePart ? `${datePart} ${timePart}` : datePart;
};

export const formatClinicalDate = (
  value,
  { locale = DEFAULT_DATE_LOCALE } = {}
) =>
  formatDisplayDate(value, {
    locale,
    format: CLINICAL_DATE_FORMAT,
    uppercaseMonth: true,
  });

/**
 * Formats a date string in PACS format (YYYYMMDD) to a human readable format
 * based on the given culture and optional timezone. If the input is not a valid date,
 * it is returned as is.
 *
 * If the "age" parameter is provided, the function will also calculate the
 * age in years and append it to the formatted date in the format "(age: X)".
 *
 * @param {string} input - Date string in PACS format (YYYYMMDD)
 * @param {string} [culture] - The culture to use for formatting the date (defaults to "en-US")
 * @param {string} [age] - If provided, the function will calculate the age in
 *                        years and append it to the formatted date
 * @param {string} [timezone] - User's timezone preference (e.g., "UTC", "Europe/Paris")
 *                             If provided, will determine date format style
 * @returns {string} Formatted date string or the original input if not a valid date
 */
export function formatDateIfDate(
  input,
  culture = DEFAULT_DATE_LOCALE,
  age = undefined
) {
  let date = getDateAsPACSFormat(input, culture);
  if (!isNaN(date.getTime())) {
    let formattedDate = formatDisplayDate(date, { locale: culture });
    if (age) {
      let ageCalculated = calculateAge(date);
      formattedDate += ` (${age} : ${ageCalculated})`;
      return formattedDate;
    }
    return formattedDate;
  }

  return input;
}

/**
 * @function parseYYYYMMDD
 * @description Parse a date string in the format 'YYYYMMDD' and return a corresponding Date object.
 * @param {string} dateString - The input date string
 * @returns {Date|null} A Date object if the input is valid, null otherwise
 */
export function parseYYYYMMDD(dateString) {
  if (!dateString || !/^\d{8}$/.test(dateString)) return null;
  const year = parseInt(dateString.substring(0, 4), 10);
  const month = parseInt(dateString.substring(4, 6), 10) - 1; // JS months are 0-indexed
  const day = parseInt(dateString.substring(6, 8), 10);
  return new Date(year, month, day);
}

/**
 * @function formatDateDDMMYYYY
 * @description Format a Date object as a string in the format DD/MM/YYYY
 * @param {Date} date - The Date object to format
 * @returns {string} The formatted date string
 */
export function formatDateDDMMYYYY(date) {
  if (!(date instanceof Date) || isNaN(date)) return "";
  // Legacy helper retained for backwards compatibility (dd/mm). Prefer formatDisplayDate.
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // JS months are 0-indexed
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * @function parseSortableDate
 * @description Parse a date string as a Date object. Tries to parse the string as an ISO string (YYYY-MM-DDTHH:mm:ss.sssZ) first, and if that fails, tries to parse it as a string in the format YYYYMMDD.
 * @param {string} dateString - The input date string
 * @returns {Date|null} A Date object if the input is valid, null otherwise
 */
export function parseSortableDate(dateString) {
  if (!dateString) return null;

  // Try parsing as ISO string (YYYY-MM-DDTHH:mm:ss.sssZ)
  let date = new Date(dateString);
  if (!isNaN(date.getTime())) return date;

  // Try parsing as YYYYMMDD format
  if (/^\d{8}$/.test(dateString)) {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    date = new Date(year, month - 1, day);
    if (!isNaN(date.getTime())) return date;
  }
  // If all parsing attempts fail, return null
  return null;
}

export function calculateAge(date) {
  if (!isNaN(date.getTime())) {
    const ageDifferance = Date.now() - date.getTime(); // difference in milliseconds
    const ageDate = new Date(ageDifferance);
    const ageCalculated = Math.abs(ageDate.getUTCFullYear() - 1970);
    return ageCalculated;
  }
  return "";
}

export function getAge(input, culture) {
  let date = getDateAsPACSFormat(input, culture);
  if (!isNaN(date.getTime())) {
    let ageCalculated = calculateAge(date);
    return ageCalculated;
  }
  return "";
}

/**
 * @function getDateAsPACSFormat
 * @description Parse a date string as a Date object. This function is used to parse a date string in the format 'YYYYMMDD'.
 * @param {string} input - The input date string
 * @param {string} culture - The culture to use when parsing the date string
 * @returns {Date} A Date object if the input is valid, null otherwise
 */
function getDateAsPACSFormat(input, culture) {
  const year = input?.substring(0, 4);
  const month = input?.substring(4, 6);
  const day = input?.substring(6, 8);
  const date = new Date(year, month - 1, day);

  return date;
}

/**
 * Converts a study creation date from ISO format to a localized date and time string.
 *
 * @param {string} input - The input date string in ISO format (e.g., "2024-09-05T15:25:30.935Z")
 * @param {string} [culture="en-US"] - The culture to use for formatting (default is "en-US")
 * @returns {string} The formatted date string (e.g., "2024/09/05 15:25:30")
 */
export function formatCreationDate(input, culture, includeTime = true) {
  const date = coerceToDate(input);
  if (!date) return input || "";

  return formatDisplayDate(date, {
    locale: culture || DEFAULT_DATE_LOCALE,
    includeTime,
  });
}
/**
 * @function dbDateFormat
 * @description Format a Date object as a string in the format 'YYYYMMDD'.
 * @param {Date} input - The Date object to format
 * @returns {string} The formatted date string
 */
export function dbDateFormat(input) {
  // check if the is already in the correct format

  if (/^\d{8}$/.test(input)) {
    return input;
  }
  try {
    const date = new Date(input);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Add leading 0 if needed
    const day = date.getDate().toString().padStart(2, "0"); // Add leading 0 if needed

    return `${year}${month}${day}`;
  } catch (error) {
    return "";
  }
}

export const normalizeToYYYYMMDD = dateValue => {
  if (!dateValue) return null;

  // If already in YYYYMMDD format, return as-is
  if (typeof dateValue === "string" && /^\d{8}$/.test(dateValue)) {
    return dateValue;
  }

  // Handle timestamp (number or numeric string)
  if (
    typeof dateValue === "string" &&
    dateValue.length > 10 &&
    /^\d+$/.test(dateValue)
  ) {
    const timestamp = parseInt(dateValue, 10);
    if (!isNaN(timestamp)) {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return dbDateFormat(date);
      }
    }
  }

  // Handle other date formats using dbDateFormat
  try {
    return dbDateFormat(dateValue);
  } catch (error) {
    return null;
  }
};

/**
 * @function formatDateAsMonthYear
 * @description Format a date string in YYYYMMDD format as a string in the format 'Month Year'.
 * @param {string|number} input - The date string in format YYYYMMDD
 * @returns {string} The formatted date string as "MONTH YEAR" abbreviated e.g. "JAN 24"
 */
export function formatDateAsMonthYear(input) {
  if (!input) return "";

  // Convert to string if it's a number
  const dateStr = input.toString();

  // Extract year, month, and day from the YYYYMMDD format
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);

  // Create a valid date string in ISO format: YYYY-MM-DD
  const isoDateStr = `${year}-${month}-${day}`;

  // Create the date object
  const date = new Date(isoDateStr);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return "INVALID DATE";
  }

  // Get the month abbreviation
  const monthAbbr = date
    .toLocaleString("default", { month: "short" })
    .toUpperCase();

  // Get last two digits of the year
  const yearShort = year.slice(-2);

  return `${monthAbbr} ${yearShort}`;
}

/**
 * Formats a timestamp to "DD MMM YYYY / HH:MM AM/PM" format
 * @param {number|string} timestamp - Unix timestamp in milliseconds
 * @returns {string} Formatted date string (e.g. "24 MAY 2024 / 01:05 PM")
 */
export const formatDateWithTime = timestamp => {
  try {
    // Handle both string and number timestamps
    const numericTimestamp =
      typeof timestamp === "string" ? parseInt(timestamp, 10) : timestamp;

    // Validate timestamp
    if (!numericTimestamp || isNaN(numericTimestamp)) {
      return "Invalid Date";
    }

    const date = new Date(numericTimestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    // Month names array
    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];

    // Get date components
    const day = String(date.getDate()).padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    // Get time components
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    // Convert to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const formattedHours = String(hours).padStart(2, "0");

    return `${day} ${month} ${year} / ${formattedHours}:${minutes} ${ampm}`;
  } catch (error) {
    return "Error formatting date";
  }
};

/**
 * Formats a timestamp with a specific timezone to "MMM DD YYYY / HH:MM AM/PM"
 * (month-first by default). Uses user preference timezone to display consistent local times.
 * @param {number|string|Date} timestamp - Timestamp or Date
 * @param {string} timeZone - IANA timezone identifier (e.g., "America/New_York")
 * @param {Object} [options] - Optional display options
 * @param {string} [options.locale] - Locale for month names (defaults to en-US)
 * @param {boolean} [options.monthFirst=true] - Set false to keep DD MMM order
 * @returns {string} Formatted date string
 */
export const formatDateWithTimeInZone = (
  timestamp,
  timeZone = "UTC",
  { locale = DEFAULT_DATE_LOCALE, monthFirst = true } = {}
) => {
  try {
    const dateObj =
      timestamp instanceof Date ? timestamp : parseTimestampField(timestamp);
    if (!dateObj || isNaN(dateObj.getTime())) return "Invalid Date";

    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const parts = formatter.formatToParts(dateObj).reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

    const day = parts.day || "";
    const month = (parts.month || "").toUpperCase();
    const year = parts.year || "";
    const hour = parts.hour || "";
    const minute = parts.minute || "";
    const dayPeriod = (parts.dayPeriod || "").toUpperCase();

    if (!day || !month || !year || !hour || !minute || !dayPeriod) {
      return formatter.format(dateObj);
    }

    const datePart = monthFirst
      ? `${month} ${day} ${year}`
      : `${day} ${month} ${year}`;

    return `${datePart} / ${hour}:${minute} ${dayPeriod}`;
  } catch (error) {
    return "Error formatting date";
  }
};

/**
 * Formats a timestamp to display date only without time.
 *
 * @param {number|string} timestamp - Unix timestamp (milliseconds)
 * @returns {string} Formatted date string (e.g. "24 MAY 2024")
 */
export const formatDateOnly = timestamp => {
  try {
    const numericTimestamp =
      typeof timestamp === "string" ? parseInt(timestamp, 10) : timestamp;

    if (!numericTimestamp || isNaN(numericTimestamp)) {
      return "Invalid Date";
    }

    const date = new Date(numericTimestamp);

    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];

    const day = String(date.getDate()).padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    return "Error formatting date";
  }
};

/**
 * Formats digital twin creation date for display in headers.
 * Supports both single twin and comparison mode.
 *
 * @param {Object|Array} twinData - Either a single twin object or array with twin(s)
 * @param {Object} [comparedTwin] - Optional compared twin for comparison mode
 * @param {Object} [options] - Optional display overrides
 * @param {string} [options.locale] - Locale for formatting
 * @param {string} [options.format] - Date format token string
 * @returns {string} Formatted creation date string or empty string if no date available
 *
 * @example
 * // Single twin
 * formatTwinCreationDate({ date_of_creation: "20240524" })
 * // Returns: "Created on: 24/05/2024"
 *
 * @example
 * // Comparison mode
 * formatTwinCreationDate(
 *   { creationDate: "20240524" },
 *   { creationDate: "20240612" }
 * )
 * // Returns: "Created on: 24/05/2024 vs 12/06/2024"
 *
 * @example
 * // Array format (from useDataBundle)
 * formatTwinCreationDate([{ date_of_creation: "20240524" }])
 * // Returns: "Created on: 24/05/2024"
 */
export const formatTwinCreationDate = (
  twinData,
  comparedTwin = null,
  options = {}
) => {
  if (!twinData) return "";

  // Handle array format (from useDataBundle in Check/Align)
  const mainTwin = Array.isArray(twinData) ? twinData[0] : twinData;
  if (!mainTwin) return "";

  // Extract creation date - support both property names
  const getCreationDate = twin => {
    return twin?.date_of_creation || twin?.creationDate;
  };

  const mainDate = getCreationDate(mainTwin);

  // Comparison mode
  if (comparedTwin) {
    const mainDateFormatted = mainDate
      ? formatDisplayDate(mainDate, {
          locale: options.locale || DEFAULT_DATE_LOCALE,
          format: options.format || DEFAULT_DATE_FORMAT,
        })
      : null;
    const comparedDate = getCreationDate(comparedTwin);
    const comparedDateFormatted = comparedDate
      ? formatDisplayDate(comparedDate, {
          locale: options.locale || DEFAULT_DATE_LOCALE,
          format: options.format || DEFAULT_DATE_FORMAT,
        })
      : null;

    if (mainDateFormatted && comparedDateFormatted) {
      return `Created on: ${mainDateFormatted} vs ${comparedDateFormatted}`;
    } else if (mainDateFormatted) {
      return `Created on: ${mainDateFormatted}`;
    } else if (comparedDateFormatted) {
      return `Created on: ${comparedDateFormatted}`;
    }
    return "";
  }

  // Single twin mode
  return mainDate
    ? `Created on: ${formatDisplayDate(mainDate, {
        locale: options.locale || DEFAULT_DATE_LOCALE,
        format: options.format || DEFAULT_DATE_FORMAT,
      })}`
    : "";
};

/**
 * Parse timestamp field that can be either a string or number
 * Commonly used for pain assessment dates and other timestamp fields
 * @param {string|number} dateValue - Timestamp as string or number (milliseconds)
 * @returns {Date|null} Parsed Date object or null if invalid
 */
export const parseTimestampField = dateValue => {
  if (!dateValue) return null;

  try {
    // Handle numeric string timestamps (e.g., "1762766746293")
    if (typeof dateValue === "string" && /^\d+$/.test(dateValue)) {
      const timestamp = parseInt(dateValue, 10);
      const dateObj = new Date(timestamp);
      if (!isNaN(dateObj.getTime())) {
        return dateObj;
      }
    }

    // Handle numeric timestamps
    if (typeof dateValue === "number") {
      const dateObj = new Date(dateValue);
      if (!isNaN(dateObj.getTime())) {
        return dateObj;
      }
    }

    // Fallback: try parsing as ISO string or other date format
    const dateObj = new Date(dateValue);
    if (!isNaN(dateObj.getTime())) {
      return dateObj;
    }
  } catch (e) {}

  return null;
};

/**
 * Format a date value to YYYY-MM-DD string format for report generation
 * This format is more readable than YYYYMMDD and is expected by the backend
 * @param {Date|string|number} dateValue - Date object, ISO string, timestamp, or YYYYMMDD string
 * @returns {string|null} Formatted date string in YYYY-MM-DD format or null if invalid
 * @example
 * formatDateForReport(new Date(2025, 0, 15)) // Returns: "2025-01-15"
 * formatDateForReport("20250115") // Returns: "2025-01-15"
 * formatDateForReport(1705276800000) // Returns: "2025-01-15"
 */
export const formatDateForReport = dateValue => {
  if (!dateValue) return null;

  try {
    let date;

    // Handle YYYYMMDD string format
    if (typeof dateValue === "string" && /^\d{8}$/.test(dateValue)) {
      const year = dateValue.substring(0, 4);
      const month = dateValue.substring(4, 6);
      const day = dateValue.substring(6, 8);
      date = new Date(year, parseInt(month, 10) - 1, day);
    } else {
      // Handle Date object, ISO string, or timestamp
      date = new Date(dateValue);
    }

    if (isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    return null;
  }
};

/**
 * Filters an array of items by a YYYYMMDD date range.
 * Start bound is inclusive at 00:00:00, end bound is inclusive at 23:59:59.999.
 * Items whose date cannot be parsed are included (passed through).
 *
 * @param {Array} items - Array to filter
 * @param {Function} getDate - Accessor returning the date value for an item
 * @param {string|null} startYYYYMMDD - Range start as YYYYMMDD string (or null = no lower bound)
 * @param {string|null} endYYYYMMDD - Range end as YYYYMMDD string (or null = no upper bound)
 * @returns {Array} Filtered array
 */
export function filterByDateRange(items, getDate, startYYYYMMDD, endYYYYMMDD) {
  const startDate = parseYYYYMMDD(startYYYYMMDD);
  const endDate = parseYYYYMMDD(endYYYYMMDD);
  if (endDate) endDate.setHours(23, 59, 59, 999);

  return items.filter(item => {
    const itemDate = parseDateValue(getDate(item));
    if (!itemDate) return true;
    return (
      (!startDate || itemDate >= startDate) &&
      (!endDate || itemDate <= endDate)
    );
  });
}
